/**
 * Service-page copy. Unknown project measurements are omitted rather than invented.
 * TODO markers for missing business facts live in docs/SEO_CONTENT_BACKLOG.md.
 */

import { BUSINESS_NAME } from '../lib/businessContact.js';

const UPDATED = '2026-10-14';

export const SERVICE_CONTENT = {
  'entertainment-rgbw': {
    updated: UPDATED,
    path: '/services/entertainment-rgbw-dmx',
    navLabel: 'RGBW & DMX Lighting',
    title: 'RGBW & DMX Lighting Systems in South Florida | Avision Lighting',
    description:
      'Permanently installed RGBW and DMX lighting for South Florida venues, trampoline parks, and recreation facilities. White light, color scenes, zones, and controls.',
    h1: 'RGBW and DMX lighting for commercial venues',
    lede: `${BUSINESS_NAME} installs permanent RGBW and DMX lighting infrastructure for commercial venues in South Florida. This is built lighting — fixtures, zones, scenes, and a control system that stay with the building — not a weekend rental package. Qualifying large-scale entertainment and specialty scopes may also be discussed for travel outside the primary market.`,
    cta: {
      label: 'Talk With Avision About RGBW Lighting',
      href: '/contact?service=entertainment-rgbw'
    },
    illustration: {
      src: '/images/illustrations/recreation-trampoline-court-illustration.jpg',
      webp: '/images/illustrations/recreation-trampoline-court-illustration.webp',
      alt: 'Illustration of an indoor trampoline court with RGBW color wash lighting. Not a documented Avision project photo.',
      caption: 'Illustration of an indoor trampoline court with RGBW color wash lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'What is RGBW commercial lighting?',
        answer:
          'RGBW commercial lighting uses red, green, blue, and a dedicated white LED channel in the same fixture family. Venues use the color channels for scenes and effects, and the white channel when the room needs to look like a normal, workable space. In a trampoline park, restaurant, or entertainment venue, that mix is what lets one installed system cover both show mode and everyday operation.',
        paragraphs: [
          'The white diode matters because a mix of red, green, and blue rarely produces a stable white. It tends to look pink, green, or gray depending on the dimming curve. A separate white source lets staff clean, reset, and operate the room under light that customers and employees can actually see by.',
          'Commercial installations use architectural-grade fixtures — linear grazers, washes, downlights, and controlled accent fixtures — mounted as part of the building. They are aimed, powered, and addressed for that room, then left in place.'
        ]
      },
      {
        question: 'What is DMX lighting?',
        answer:
          'DMX is a lighting control protocol. A controller sends channel data to fixtures that have been given start addresses, and each channel typically drives one function such as intensity, red, green, blue, white, or a strobe. In a commercial building, DMX is how color, scenes, and effects are programmed across zones instead of relying on a single on/off switch.',
        paragraphs: [
          'A DMX universe carries 512 channels. Larger rooms are split across universes so a fixture’s address does not collide with another. Zones are groups of fixtures that should act together — a court, a party room, a facade, a concession line — even if they are not on the same circuit.',
          'Scenes are stored looks: white work light, a branded color, a slow fade, a game-day look. Effects are changes over time. The controller, the addressing, and the fixture manual have to agree or the room will not reproduce the scene that was programmed.'
        ]
      },
      {
        question: 'What is the difference between RGB and RGBW?',
        answer:
          'RGB fixtures mix red, green, and blue only. RGBW fixtures add a dedicated white LED. RGB can create saturated color, but its “white” is a compromise. RGBW can hold a usable white for cleaning, turnover, and ordinary operation, then shift to color when the venue wants a scene.',
        paragraphs: [
          'That difference shows up in recreation and hospitality projects more than in a one-night stage plot. A park that runs birthday parties all day cannot live on saturated color alone. Parents, staff, and cameras need a white mode that does not look muddy. RGBW is the practical commercial answer when the same fixtures have to do both jobs.',
          'Avision’s longer comparison is in the RGB vs RGBW guide. The short version used on quotes is: if the room must function as a workplace between shows, specify a white channel.'
        ]
      },
      {
        question: 'Can commercial RGBW lights produce normal white lighting?',
        answer:
          'Yes, when the fixture includes a real white channel and the control system has a white scene programmed on purpose. The white will not match every interior lamp in the building unless color temperature is specified — 3000K, 4000K, and 5000K whites look different — but it can be a normal operating light rather than a tinted mix of red, green, and blue.',
        paragraphs: [
          'White-light operation should be treated as a design requirement, not a side effect. During layout, decide which zones must reach a usable white, which can stay color-only, and how staff will recall that scene without a lighting console operator on site.'
        ]
      },
      {
        question: 'How is DMX lighting controlled?',
        answer:
          'A commercial DMX system is controlled by a processor or console that stores scenes and sends them to addressed fixtures. Staff usually recall those scenes from a wall station, a touch panel, or a simple preset — not by editing channel values during business hours. The programming happens at commissioning.',
        paragraphs: [
          'Control architecture is the unglamorous part that decides whether the system is operable. It includes the controller location, how universes are distributed, where power supplies live, how zones are grouped, and what happens if the controller reboots. A facade, a party room, and a court should be separate zones if they need independent looks.',
          'DMX is not the same tool as a 0–10V dimmer. 0–10V is typically one intensity level for a zone of white fixtures. It does not carry color channels or effects. Many buildings use both: 0–10V or a relay for ordinary white lighting, and DMX where color and scenes are required.'
        ]
      },
      {
        question: 'What businesses benefit from DMX lighting?',
        answer:
          'DMX earns its place where the lighting has to change on purpose: trampoline parks and indoor recreation, fitness studios, event venues with a permanent rig, entertainment dining, facades that shift color, and sports or recreation spaces that want a fan-experience look on top of playable white light. A warehouse that only needs even illumination usually does not need DMX.',
        paragraphs: [
          'The common thread is a permanently installed system with more than one stored look. If the operator cannot describe a second scene, a simpler control method is the better specification.'
        ]
      },
      {
        question: 'Can an existing commercial facility be converted to RGBW?',
        answer:
          'Often yes, but it is a lighting and electrical project, not a lamp swap. Existing junction boxes, mounting heights, circuit capacity, and control wiring decide what can be reused. Color fixtures also need a plan for white-light mode so the room is still usable when it is not in a scene.',
        paragraphs: [
          'Avision’s published project photos show that kind of conversion on a Sky Zone facade: a white architectural wash replaced by blue and violet light on the same arched entry. Fixture counts, circuit maps, and the control protocol for that project were not recorded with the photos. Interior recreation video on this site shows color already in the room; it is not a measured before-and-after retrofit study.',
          'A useful first conversation covers room photos, a fixture count if one exists, how the space operates during the day, and whether color is for the whole room or for selected zones.'
        ]
      }
    ],
    related: [
      { href: '/services/recreation-lighting', label: 'Indoor recreation lighting' },
      { href: '/services/sports-lighting', label: 'Sports lighting systems' },
      { href: '/services/lighting-controls', label: 'DMX and 0–10V lighting controls' },
      { href: '/projects/sky-zone-facade-lighting', label: 'Sky Zone facade lighting project' },
      { href: '/guides/rgb-vs-rgbw', label: 'RGB vs RGBW lighting guide' }
    ]
  },
  'commercial-led': {
    updated: UPDATED,
    path: '/services/commercial-led',
    navLabel: 'Commercial LED Retrofits',
    title: 'Commercial LED Retrofit in South Florida | Avision Lighting',
    description:
      'How Avision Lighting designs a commercial LED retrofit in South Florida: fixture audit, light levels, replacement layout, controls, installation, and commissioning.',
    h1: 'Commercial LED retrofits for South Florida facilities',
    lede: `${BUSINESS_NAME} replaces aging commercial lighting with LED systems for facilities in Palm Beach, Broward, and Miami-Dade counties. A retrofit is a design problem first: what is installed, what light level the work needs, and what controls the building will actually use.`,
    cta: {
      label: 'Request a Retrofit Evaluation',
      href: '/contact?service=commercial-led'
    },
    illustration: {
      src: '/images/illustrations/warehouse-high-bay-illustration.jpg',
      webp: '/images/illustrations/warehouse-high-bay-illustration.webp',
      alt: 'Illustration of a commercial facility under LED high-bay lighting. Not a documented Avision project photo.',
      caption: 'Illustration of a commercial facility under LED high-bay lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'Is an LED retrofit worth it for a warehouse?',
        answer:
          'It is worth pricing when the existing system is metal halide, fluorescent, or an early LED product that has dimmed, shifted color, or become expensive to maintain. Whether it is worth doing depends on fixture count, hours of operation, mounting height, and the shape of the space — not on a generic savings percentage. Avision does not publish a universal energy-savings claim.',
        paragraphs: [
          'Warehouses that run long shifts usually feel the maintenance difference first: fewer lamp changes at height. Energy use can drop when high-wattage HID fixtures are replaced with LED at a comparable light level, but the number is a calculation from the existing wattage, the proposed wattage, and the operating schedule. Without those inputs, a percentage is a guess.',
          'A short evaluation starts with photos of a typical bay, the lamp or fixture wattage printed on a nameplate, a rough count, and the ceiling height.'
        ]
      },
      {
        question: 'Can existing high-bay fixtures be replaced with LED?',
        answer:
          'Yes. High-bay LED fixtures are a standard replacement for HID high bays when the structure can carry the new fixture and the circuit can feed it. The mounting point can often be reused. The optical distribution usually should not be copied blindly, because an LED high bay throws light differently than the reflector it replaces.',
        paragraphs: [
          'Spacing, aisle width, and rack height decide the photometrics. A one-for-one swap that ignores the racking layout can leave the top of a pick face bright and the floor dark, or the reverse. That is why a retrofit design includes a layout, not only a model number.'
        ]
      },
      {
        question: 'How is a commercial lighting retrofit designed?',
        answer:
          'Avision designs a retrofit in a fixed order: audit the existing fixtures, count them, record wattage, assess light levels against the task, select replacements, check photometrics and controls, install, then commission. The output is a scope a facility manager or general contractor can build from, not a catalog page.',
        paragraphs: [
          'Existing fixture audit means type, mounting height, condition, and whether the housing stays. Fixture count and wattage are the basis for any later energy comparison. Light-level assessment is what the task needs — storage, packing, inspection, retail floor — not a single number for every building.',
          'Replacement design covers distribution, color temperature, glare, and controls. Photometric considerations include spacing and obstructions. Controls may be as simple as zoning and occupancy sensing, or as involved as daylight response. Installation and commissioning confirm aiming, controls, and that the scenes staff were promised actually recall.'
        ]
      },
      {
        question: 'What information is needed before recommending replacement fixtures?',
        answer:
          'The useful packet is a fixture count, the existing wattage from a nameplate or lamp type, mounting height, photos down a typical aisle or bay, the hours the lights run, and what the space is used for. A floor plan or reflected ceiling plan makes the recommendation specific. Without a count and a height, fixture selection is still a guess.',
        paragraphs: [
          'Voltage, emergency-lighting requirements, and whether controls must tie into an existing building system also change the specification. Those details are part of the evaluation, not assumptions.'
        ]
      },
      {
        question: 'How are light levels determined?',
        answer:
          'Light levels are determined from the task and the geometry of the room, then checked with a photometric layout. There is no single foot-candle target for every commercial building. Bulk storage, fine assembly, a retail aisle, and a sports court are different problems, and South Florida daylight at a loading door changes the picture again.',
        paragraphs: [
          'A photometric study is a calculation of how the selected fixtures, at a stated mounting height and spacing, deliver light to the work plane. It is not a photograph and it is not a savings estimate. Avision explains the method in project conversations; this site does not yet include a completed photometric report from a named facility.'
        ]
      }
    ],
    related: [
      { href: '/guides/commercial-lighting-cost', label: 'Commercial lighting cost planning guide' },
      { href: '/services/warehouse-lighting', label: 'Warehouse and high-bay lighting' },
      { href: '/services/lighting-controls', label: 'Lighting controls' },
      { href: '/services/parking-lot-lighting', label: 'Parking lot LED lighting' },
      { href: '/guides/led-retrofit-evaluation', label: 'What a retrofit evaluation includes' }
    ]
  },
  warehouse: {
    updated: UPDATED,
    path: '/services/warehouse-lighting',
    navLabel: 'Warehouse Lighting',
    title: 'Warehouse & High-Bay LED Lighting in South Florida | Avision Lighting',
    description:
      'Warehouse and high-bay LED lighting for South Florida distribution and storage buildings. Layout, mounting height, aisles, and controls — without generic savings claims.',
    h1: 'Warehouse and high-bay lighting',
    lede: `${BUSINESS_NAME} lights warehouses and industrial interiors in South Florida. The work is even, maintainable illumination in aisles and open floors: the right distribution at the real mounting height, with controls that match how the building operates.`,
    cta: {
      label: 'Request a Retrofit Evaluation',
      href: '/contact?service=warehouse'
    },
    illustration: {
      src: '/images/illustrations/warehouse-high-bay-illustration.jpg',
      webp: '/images/illustrations/warehouse-high-bay-illustration.webp',
      alt: 'Illustration of a warehouse aisle under LED high-bay lighting. Not a documented Avision project photo.',
      caption: 'Illustration of a warehouse aisle under LED high-bay lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'What matters in a warehouse lighting layout?',
        answer:
          'Mounting height, aisle width, rack height, and the task on the floor decide the layout. A high-bay fixture that looks correct in an empty rendering can fail once pallet racking blocks the beam. Spacing is a consequence of those dimensions and the fixture’s distribution, not a fixed number of feet copied from another building.',
        paragraphs: [
          'Open storage, racked aisles, and pick modules want different optics. A wide distribution in a tall racked aisle dumps light on the top of the rack. A narrower aisle optic is there to push light down the pick face. The fixture count follows that choice.',
          'Avision has not published a measured warehouse case study with fixture counts and wattages on this site. The design sequence is the same one used for commercial LED retrofits: audit, count, wattage, light-level assessment, layout, controls, installation, commissioning.'
        ]
      },
      {
        question: 'Can warehouse lights be controlled without a complicated system?',
        answer:
          'Yes. Many warehouses only need zoning, a time schedule, and occupancy or vacancy sensing in aisles that are empty for long stretches. That is usually 0–10V dimming or switched zones, not DMX. DMX belongs where color or programmed effects are part of the operation, which is uncommon in a distribution building.',
        paragraphs: [
          'Controls should be commissioned with the people who close the building at night. A sensor layout that turns aisles off during a live pick is a failed control design, even if the energy model looked better.'
        ]
      },
      {
        question: 'What information is needed for a warehouse lighting recommendation?',
        answer:
          'Ceiling or deck height, a fixture or lamp wattage, a count or a bay size that can produce a count, photos down an aisle, and whether the racks are in yet. Voltage and emergency lighting need to be identified before a replacement is ordered. Operating hours matter only if someone asks for an energy comparison — and that comparison should use those hours, not an assumed schedule.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/guides/commercial-lighting-cost', label: 'Commercial lighting cost planning guide' },
      { href: '/services/commercial-led', label: 'Commercial LED retrofits' },
      { href: '/services/lighting-controls', label: 'Lighting controls' },
      { href: '/guides/led-retrofit-evaluation', label: 'Retrofit evaluation guide' },
      { href: '/services/parking-lot-lighting', label: 'Parking lot lighting' }
    ]
  },
  sports: {
    updated: UPDATED,
    path: '/services/sports-lighting',
    navLabel: 'Sports Lighting',
    title: 'Sports Lighting Systems in South Florida | Avision Lighting',
    description:
      'Sports lighting for South Florida fields, courts, gyms, and recreation facilities, including white play light, RGBW fan-experience lighting, and DMX controls where they belong.',
    h1: 'Sports lighting systems in South Florida',
    lede: `${BUSINESS_NAME} designs sports and athletic lighting for fields, courts, gyms, and indoor recreation in South Florida. Playable white light is the baseline. RGBW and DMX are added when the facility also wants color, scenes, or a fan-experience look.`,
    cta: {
      label: 'Request a Photometric / Lighting Review',
      href: '/contact?service=sports'
    },
    illustration: {
      src: '/images/illustrations/sports-court-lighting-illustration.jpg',
      webp: '/images/illustrations/sports-court-lighting-illustration.webp',
      alt: 'Illustration of an indoor sports court under commercial LED lighting. Not a documented Avision project photo.',
      caption: 'Illustration of an indoor sports court under commercial LED lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'How is sports lighting designed?',
        answer:
          'Sports lighting is designed from the playing surface outward. The target is uniform light on the court or field at the level the sport and the broadcast or city requirement call for, with glare kept out of the players’ eyes. Pole or mounting locations, aiming angles, and fixture photometrics are the design. A fixture count without an aiming plan is not a sports lighting design.',
        paragraphs: [
          'Indoor courts and gyms are a different geometry from outdoor fields. Ceiling height, structure, and existing rigging decide what can be mounted. Outdoor fields add wind, aiming access, spill light at the property line, and coastal corrosion in South Florida.',
          'Avision does not publish a single foot-candle number as a guarantee. Levels depend on the sport, the class of play, and any adopted standard for that project. A photometric review is how those levels are checked before equipment is ordered.'
        ]
      },
      {
        question: 'Can existing sports-light poles be reused?',
        answer:
          'Sometimes. Reuse depends on pole condition, foundation, height, and whether the new fixtures’ weight and wind area are within what the pole was built for. A pole that is tall enough for the previous metal-halide flood is not automatically correct for a new LED aiming scheme. If those structural facts are not documented, the honest answer is that the pole needs to be evaluated before it is reused.',
        paragraphs: [
          'Electrical capacity is the other half. New LED loads are often lower, but controls, drivers, and any RGBW heads still need a real circuit plan. Avision will not claim a pole can be reused without that review.'
        ]
      },
      {
        question: 'What is RGBW sports lighting?',
        answer:
          'RGBW sports lighting keeps a white mode that is actually used for play, and adds color channels for introductions, intermissions, or events. The white channel is not optional if people are competing under the same fixtures. Color-only sports lighting is a show system, not a playing-surface system.',
        paragraphs: [
          'Indoor recreation often wants this combination more than a city softball field does. A gym or court inside an entertainment venue may run white light for open play and a programmed color scene for a league night or a party. That is a controls problem as much as a fixture problem.'
        ]
      },
      {
        question: 'Can sports lights be controlled through DMX?',
        answer:
          'Yes, when the fixtures and the staff interface are specified for it. DMX can recall white play light, a team color, and an effect, zone by zone. It is the wrong default for a simple outdoor field that only needs on, off, and maybe a dimmed practice level — those fields are usually better on a dedicated sports control or a simple relay and dimming scheme.',
        paragraphs: [
          'If DMX is used, addressing, universes, and a staff-facing preset matter more than the console brand. The people closing the gym should be able to return the room to white light without a programmer.'
        ]
      }
    ],
    related: [
      { href: '/services/recreation-lighting', label: 'Recreation facility lighting' },
      { href: '/services/entertainment-rgbw-dmx', label: 'Explore RGBW & DMX lighting' },
      { href: '/services/lighting-controls', label: 'Lighting controls' },
      { href: '/projects/sky-zone-interior-lighting', label: 'Sky Zone interior lighting project' }
    ]
  },
  recreation: {
    updated: UPDATED,
    path: '/services/recreation-lighting',
    navLabel: 'Recreation Lighting',
    title: 'Indoor Recreation & Trampoline Park Lighting | Avision Lighting',
    description:
      'Lighting for trampoline parks, indoor recreation, and fitness studios in South Florida. Permanent RGBW and white-light operation, with published Sky Zone project media.',
    h1: 'Recreation and trampoline park lighting',
    lede: `${BUSINESS_NAME} lights indoor recreation facilities — trampoline parks, party rooms, fitness studios, and similar venues — across South Florida. These rooms need white light for staff and daytime operation, and color, zones, and scenes when the floor is in show mode.`,
    cta: {
      label: 'Talk With Avision About RGBW Lighting',
      href: '/contact?service=entertainment-rgbw'
    },
    illustration: {
      src: '/images/illustrations/recreation-trampoline-court-illustration.jpg',
      webp: '/images/illustrations/recreation-trampoline-court-illustration.webp',
      alt: 'Illustration of an indoor trampoline park under colored recreation lighting. Not a documented Avision project photo.',
      caption: 'Illustration of an indoor trampoline park under colored recreation lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },
    images: [
      {
        src: '/images/projects/indoor-recreation-color-spot-lighting.jpg',
        webp: '/images/projects/indoor-recreation-color-spot-lighting.webp',
        alt: 'Documented indoor recreation space with colored spot lighting',
        caption: 'Documented recreation lighting still from Avision project media.',
        width: 1536,
        height: 2048
      }
    ],
    answers: [
      {
        question: 'How should a trampoline park be lit?',
        answer:
          'A trampoline park needs at least two operating modes from the installed lighting: a white or high-visibility mode for opening, cleaning, and inspections, and a color mode for parties and night sessions. Courts, foam pits, party rooms, and the entry should be separate zones so a birthday room does not force the whole floor into one look.',
        paragraphs: [
          'UV and strobe effects show up in entertainment installs, including party-room video on this site, but they are accents. They do not replace a layout that staff can return to a readable white. Fixtures over courts also have to survive impact-adjacent mounting and regular maintenance at height.',
          'Published Avision media includes a Sky Zone facade, Sky Zone interior walk-throughs, and party-room color lighting. Fixture counts and a DMX address schedule were not filed with that media.'
        ]
      },
      {
        question: 'Is recreation lighting the same as sports lighting?',
        answer:
          'No. Sports lighting is judged on uniformity and glare for a specific game. Recreation lighting is judged on whether the attraction can change scenes and still be operated safely between them. A court inside a trampoline park may need both: playable light and a programmed color scene. Those requirements should be written down separately so the color system is not asked to fake a white mode it does not have.',
        paragraphs: []
      },
      {
        question: 'Can a fitness studio use RGBW lighting?',
        answer:
          'Yes. Studios use white light for instruction and color scenes for classes that are sold as an experience. The control preset has to be simple enough for an instructor to recall between sessions. DMX is appropriate when color is part of the product. A single dimmed white zone is enough when it is not.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/services/entertainment-rgbw-dmx', label: 'Explore RGBW & DMX lighting' },
      { href: '/services/sports-lighting', label: 'Sports lighting systems' },
      { href: '/projects/sky-zone-facade-lighting', label: 'Sky Zone facade lighting project' },
      { href: '/projects/sky-zone-interior-lighting', label: 'Sky Zone interior lighting project' },
      { href: '/projects/party-room-color-lighting', label: 'Party room color lighting' }
    ]
  },
  architectural: {
    updated: UPDATED,
    path: '/services/architectural-lighting',
    navLabel: 'Architectural Lighting',
    title: 'Architectural Lighting in South Florida | Avision Lighting',
    description:
      'Commercial architectural and facade lighting in South Florida. Appearance, usability, durable exterior hardware, and controls — including color-changing facades.',
    h1: 'Architectural lighting for commercial buildings',
    lede: `${BUSINESS_NAME} designs architectural lighting where appearance, everyday usability, and commercial durability meet. In South Florida that usually means facades, entries, and accent lighting that have to survive sun, rain, and salt air — and still be controllable.`,
    cta: {
      label: 'Discuss a Commercial Lighting Project',
      href: '/contact?service=architectural'
    },
    illustration: {
      src: '/images/illustrations/facade-rgbw-concept-illustration.jpg',
      webp: '/images/illustrations/facade-rgbw-concept-illustration.webp',
      alt: 'Illustration of a commercial building facade with color-changing architectural lighting. Not a documented Avision project photo.',
      caption: 'Illustration of a commercial building facade with color-changing architectural lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'What is architectural lighting?',
        answer:
          'Architectural lighting is lighting planned as part of how a building is seen and used: facade washes, entry emphasis, interior accent, and the white light people still need to walk, work, and photograph the space. It is not a substitute phrase for any commercial fixture. The design question is which surfaces should read at night, and which lights have to stay neutral so the architecture stays visible.',
        paragraphs: [
          'On commercial projects the fixtures also have to be serviceable. A graze that cannot be re-lamped or cleaned without a lift every quarter is a maintenance problem wearing a design fee.'
        ]
      },
      {
        question: 'How is facade lighting controlled?',
        answer:
          'Facade lighting is controlled in zones — typically by elevation, entry, and landscape — so the building can run a steady white or brand wash most nights and a different scene when there is a reason. Color-changing facades generally use DMX or a dedicated color controller. A single photocell is enough only when the facade has one look: on at dusk, off at dawn.',
        paragraphs: [
          'Staff should be able to recall the ordinary scene without a console. The programming visit is where those presets get written and labeled.'
        ]
      },
      {
        question: 'Can architectural lights change colors?',
        answer:
          'Yes. Color-changing facade lighting is a normal commercial scope when the fixtures are specified for it and the controller can store the scenes. RGBW is preferable to RGB when the facade also needs a clean white wash for ordinary nights. Avision’s Sky Zone facade photos show a white architectural wash and a later blue-and-violet wash on the same entry. The control hardware used there was not recorded with the photos.',
        paragraphs: []
      },
      {
        question: 'What fixtures work in South Florida outdoor environments?',
        answer:
          'Exterior fixtures in South Florida need a wet-location or wet-rated housing, hardware that will not disappear in salt air, and drivers that can take the heat. Closer to the coast, corrosion resistance matters as much as the beam. IP ratings describe dust and water protection; they do not by themselves describe salt performance. Avision’s exterior guide covers how those ratings should be read.',
        paragraphs: [
          'This site does not claim a specific marine coating or a manufacturer certification. Those specifications belong on the submittal for the project, after the exposure of that site is known.'
        ]
      }
    ],
    related: [
      { href: '/services/hospitality-retail-lighting', label: 'Hospitality lighting' },
      { href: '/services/landscape-lighting', label: 'Commercial landscape lighting' },
      { href: '/services/lighting-controls', label: 'Lighting controls' },
      { href: '/projects/sky-zone-facade-lighting', label: 'Sky Zone facade lighting project' },
      { href: '/guides/florida-exterior-lighting', label: 'South Florida exterior lighting guide' }
    ]
  },
  hospitality: {
    updated: UPDATED,
    path: '/services/hospitality-retail-lighting',
    navLabel: 'Hospitality Lighting',
    title: 'Hospitality & Restaurant Lighting in South Florida | Avision Lighting',
    description:
      'Restaurant, hotel, and hospitality lighting in South Florida: dimmable white light for service, accent and facade lighting, and color only where the room is designed for it.',
    h1: 'Hospitality and restaurant lighting',
    lede: `${BUSINESS_NAME} lights restaurants, hotels, and other hospitality spaces in South Florida. The job is a room people can dine and work in, with dimming and accent that support the design — plus color or facade scenes only where the operator actually wants them.`,
    cta: {
      label: 'Discuss a Commercial Lighting Project',
      href: '/contact?service=hospitality'
    },
    illustration: {
      src: '/images/illustrations/hospitality-dining-illustration.jpg',
      webp: '/images/illustrations/hospitality-dining-illustration.webp',
      alt: 'Illustration of a hospitality dining room with warm commercial lighting. Not a documented Avision project photo.',
      caption: 'Illustration of a hospitality dining room with warm commercial lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'What makes hospitality lighting different from venue RGBW?',
        answer:
          'A dining room fails if the only look is a saturated color scene. Hospitality lighting starts with dimmable white light at the table, the pass, and the circulation, then adds accent, cove, or a color moment at the bar or facade. RGBW belongs where the concept calls for a changeable scene, not as the default for every downlight.',
        paragraphs: [
          'Controls are usually presets on a simple station: day, dinner, and close. Staff will not run a console between seatings. If the system needs a technician to brighten the room for cleanup, the control architecture is wrong.',
          'Avision has not published a documented restaurant case study with fixture counts. Illustrations on the older gallery are not project photography and are labeled that way.'
        ]
      },
      {
        question: 'Can a restaurant facade change color?',
        answer:
          'Yes, using the same approach as other architectural facade lighting: exterior-rated fixtures, separate zones for the facade and the interior, and scenes that include a restrained everyday look. Interior guest lighting and exterior color should not be tied to one button unless that is a deliberate choice.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/services/architectural-lighting', label: 'Architectural lighting' },
      { href: '/services/entertainment-rgbw-dmx', label: 'Explore RGBW & DMX lighting' },
      { href: '/services/lighting-controls', label: 'Lighting controls' },
      { href: '/service-area', label: 'South Florida service area' }
    ]
  },
  controls: {
    updated: UPDATED,
    path: '/services/lighting-controls',
    navLabel: 'Lighting Controls',
    title: 'Commercial Lighting Controls: DMX and 0–10V | Avision Lighting',
    description:
      'How Avision Lighting specifies commercial lighting controls in South Florida. DMX for color and scenes, 0–10V for white-light dimming, and why the staff interface matters.',
    h1: 'Commercial lighting controls',
    lede: `${BUSINESS_NAME} specifies lighting controls as part of the installation, not as an add-on quote. The right system is the one the building staff can recall: 0–10V or switched zones for ordinary white light, and DMX where color, effects, and multiple scenes are the point.`,
    cta: {
      label: 'Discuss a Commercial Lighting Project',
      href: '/contact?service=entertainment-rgbw'
    },
    illustration: {
      src: '/images/illustrations/lighting-controls-panel-illustration.jpg',
      webp: '/images/illustrations/lighting-controls-panel-illustration.webp',
      alt: 'Illustration of a commercial lighting control panel. Not a documented Avision project photo.',
      caption: 'Illustration of a commercial lighting control panel. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'What is the difference between DMX and 0–10V?',
        answer:
          '0–10V is an analog dimming method. A low-voltage signal tells compatible drivers how bright to be, usually as one level for a zone of white fixtures. DMX is a digital protocol that carries hundreds of channels so a controller can set color, intensity, and effects on addressed fixtures independently. They solve different problems and are often both present in the same commercial building.',
        paragraphs: [
          'Using DMX to dim a warehouse is unnecessary complexity. Using 0–10V to run a color facade does not work, because the signal has no channels for red, green, blue, and white. The specification should name the protocol per zone instead of picking one brand for the whole site.',
          'A deeper walk-through of DMX addressing is in the DMX addressing guide. The operational requirement is the same in both cases: after commissioning, a person who was not at the programming meeting must be able to get the intended scene back.'
        ]
      },
      {
        question: 'How is DMX addressing used in a building?',
        answer:
          'Each fixture is given a start address. That address is the first channel the controller will use for it, and the fixture’s mode decides how many channels it consumes. If two fixtures overlap, one of them will do the wrong thing. Universes are added when the channel count exceeds 512 or when the wiring geography makes a split cleaner.',
        paragraphs: [
          'Zones are a design idea layered on top of addresses. A zone might be “party room” or “north facade,” made of several fixtures that share a scene even if their addresses differ. Writing the address schedule down is part of commissioning. Avision’s published projects do not yet include a sample address schedule.'
        ]
      },
      {
        question: 'Who operates the system after installation?',
        answer:
          'Facility staff, not a programmer, operate it. Wall stations, labeled presets, and a short handover are part of the scope of a commercial control system. A console left in a manager’s office with unlabeled cues is not a finished installation.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/services/entertainment-rgbw-dmx', label: 'Explore RGBW & DMX lighting' },
      { href: '/guides/dmx-addressing', label: 'How DMX addressing works' },
      { href: '/services/commercial-led', label: 'Commercial LED retrofits' },
      { href: '/services/sports-lighting', label: 'Sports lighting systems' }
    ]
  },
  'parking-lot': {
    updated: UPDATED,
    path: '/services/parking-lot-lighting',
    navLabel: 'Parking Lot Lighting',
    title: 'Parking Lot LED Lighting in South Florida | Avision Lighting',
    description:
      'Parking lot and site lighting for South Florida commercial properties. Pole review, light levels, cutoff, and coastal hardware — without an invented savings figure.',
    h1: 'Parking lot and site lighting',
    lede: `${BUSINESS_NAME} replaces and installs parking-lot and site lighting for commercial properties in South Florida. The design questions are pole condition, the light level on the pavement and at the property line, and hardware that can live in heat, rain, and salt air.`,
    cta: {
      label: 'Request a Retrofit Evaluation',
      href: '/contact?service=parking-lot'
    },
    illustration: {
      src: '/images/illustrations/parking-lot-pole-illustration.jpg',
      webp: '/images/illustrations/parking-lot-pole-illustration.webp',
      alt: 'Illustration of a commercial parking lot with LED pole lighting. Not a documented Avision project photo.',
      caption: 'Illustration of a commercial parking lot with LED pole lighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },

    answers: [
      {
        question: 'How is a parking lot lighting retrofit approached?',
        answer:
          'The same way as an interior retrofit, with exterior constraints added. Count the poles and heads, record wattage, note mounting height, and look at the pavement and the neighbors before choosing a replacement. A one-for-one LED head can raise glare or spill even while it lowers wattage, so distribution and shielding are part of the selection.',
        paragraphs: [
          'Existing poles are reused only after condition and wind loading are considered. Avision does not assume a pole is adequate because it is already standing. Energy comparisons, if requested, use the recorded wattage and a stated operating schedule. No default savings percentage is offered.',
          'South Florida exterior exposure is covered in the exterior lighting guide: wet ratings, corrosion, and why an IP number is not a coastal specification by itself.'
        ]
      },
      {
        question: 'What should owners gather before a site visit?',
        answer:
          'A pole count, a photo of a typical head and its nameplate, the hours the lot stays lit, and any complaint about dark areas or light spilling onto adjacent property. Those four items are enough to start a serious evaluation.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/services/commercial-led', label: 'Commercial LED retrofits' },
      { href: '/services/architectural-lighting', label: 'Architectural lighting' },
      { href: '/guides/florida-exterior-lighting', label: 'South Florida exterior lighting guide' },
      { href: '/service-area', label: 'South Florida service area' }
    ]
  },
  landscape: {
    updated: UPDATED,
    path: '/services/landscape-lighting',
    navLabel: 'Landscape Lighting',
    title: 'Commercial Landscape Lighting in South Florida | Avision Lighting',
    description:
      'Commercial landscape and grounds lighting in South Florida, coordinated with facade and site lighting and specified for wet, coastal conditions.',
    h1: 'Commercial landscape lighting',
    lede: `${BUSINESS_NAME} installs landscape lighting as part of commercial grounds and facade work in South Florida, not as a residential path-light package. Plant material, entries, and signage are lit so they still make sense next to the building lighting.`,
    cta: {
      label: 'Discuss a Commercial Lighting Project',
      href: '/contact?service=landscape'
    },
    illustration: {
      src: '/images/illustrations/landscape-pathway-lighting-illustration.jpg',
      webp: '/images/illustrations/landscape-pathway-lighting-illustration.webp',
      alt: 'Illustration of a commercial landscape pathway with warm LED path and palm uplighting. Not a documented Avision project photo.',
      caption: 'Illustration of a commercial landscape pathway with warm LED path and palm uplighting. Not a documented Avision project photo.',
      width: 1280,
      height: 720
    },
    answers: [
      {
        question: 'How is commercial landscape lighting different from residential?',
        answer:
          'Commercial grounds lighting has to survive public use, irrigation, and a maintenance crew that was not at the install. Fixtures are specified for wet locations, transformers and wiring are located where they can be serviced, and the scenes match the facade rather than competing with it. Salt air near the coast is a materials problem, covered in the exterior lighting guide.',
        paragraphs: [
          'Color-changing landscape lighting is possible and should be zoned separately from path and step lights that need to stay a stable white for safety.'
        ]
      }
    ],
    related: [
      { href: '/services/architectural-lighting', label: 'Architectural lighting' },
      { href: '/services/parking-lot-lighting', label: 'Parking lot lighting' },
      { href: '/guides/florida-exterior-lighting', label: 'South Florida exterior lighting guide' }
    ]
  },
  maintenance: {
    updated: UPDATED,
    path: '/services/lighting-maintenance',
    navLabel: 'Lighting Maintenance',
    title: 'Commercial Lighting Maintenance in South Florida | Avision Lighting',
    description:
      'Maintenance for commercial and entertainment lighting in South Florida, including dust-fouled moving lights and high-mounted fixtures that need cleaning without a lift every visit.',
    h1: 'Commercial lighting maintenance',
    lede: `${BUSINESS_NAME} maintains commercial and entertainment lighting after the install. Dust, heat, and aiming drift take output away long before a diode “burns out.” The published evidence on this site is a dust-packed moving-light fixture and the cleaning tools used to reach high-mounted equipment.`,
    cta: {
      label: 'Request a Lighting Assessment',
      href: '/contact?service=maintenance'
    },
    images: [
      {
        src: '/images/projects/dust-covered-kinta-fx-moving-light.jpg',
        webp: '/images/projects/dust-covered-kinta-fx-moving-light.webp',
        alt: 'Dust-covered Kinta FX moving-light fixture on a work surface',
        caption: 'Documented maintenance photo: dust-packed entertainment fixture.',
        width: 1536,
        height: 2048
      },
      {
        src: '/images/proprietary-device-1.jpg',
        webp: '/images/proprietary-device-1.webp',
        alt: 'Telescopic lighting-maintenance tool standing indoors',
        caption: 'Documented maintenance photo: telescopic cleaning tool used at height.',
        width: 2048,
        height: 1536
      }
    ],
    answers: [
      {
        question: 'Why do entertainment fixtures lose output?',
        answer:
          'Moving lights and effect fixtures pull air across optics and electronics. In a trampoline park or similar venue, that air carries dust and fiber that packs the heatsink, the fans, and the lens. The photograph on this page shows a Kinta FX fixture in that condition. Cleaning is part of keeping the programmed scenes looking the way they did at commissioning.',
        paragraphs: [
          'Avision’s gallery shows a rechargeable telescopic dusting tool used to blow off and dust high fixtures without bringing in a lift for every visit. Whether that tool is a proprietary product is not documented in the site records and is not claimed here as a patent or an exclusive method.',
          'Maintenance scope — cleaning versus repair versus reprogramming — should be agreed per visit. This page does not quote a contract interval or a warranty term.'
        ]
      },
      {
        question: 'Does maintenance include controls?',
        answer:
          'It can. Scenes that no longer match the room are often an addressing, mode, or failed-fixture problem, not a “bulb.” A maintenance visit on a DMX system should check that presets still recall, not only that the lamps turn on.',
        paragraphs: []
      }
    ],
    related: [
      { href: '/projects/entertainment-fixture-maintenance', label: 'Fixture cleaning photos' },
      { href: '/services/entertainment-rgbw-dmx', label: 'Explore RGBW & DMX lighting' },
      { href: '/services/recreation-lighting', label: 'Recreation lighting' }
    ]
  }
};

export function getService(routeKey) {
  return SERVICE_CONTENT[routeKey] || null;
}

export function getServiceByPath(path) {
  return (
    Object.entries(SERVICE_CONTENT).find(([, page]) => page.path === path) || null
  );
}
